import { redirect } from '@tanstack/react-router';
import { getSubmissionData } from '../api/submissions/get-by-public-id';
import {
  UserType, SurveySettings, Submission,
} from '../types';
import {
  ApiError, isNotFoundError, isUnauthorizedError, isBadRequestError, NotFoundErrorCode,
} from './errors';
import { getSettingsByUrlSlug } from '../api/settings/get-by-url-slug';

export interface SubmissionSearchParams {
  token?: string;
}

interface SubmissionLoaderParams {
  params: { accountSlug: string; submissionId: string; token?: string };
  userType: UserType;
  deps: SubmissionSearchParams;
  redirectPath?: (accountSlug: string, submissionId: string) => string;
}

export interface SubmissionLoaderResponse {
  settings: SurveySettings;
  submission?: Submission;
  error?: ApiError;
}

async function submissionLoader ({
  params: {
    accountSlug,
    submissionId,
    token: routeToken,
  },
  userType,
  deps: { token: searchToken },
  redirectPath,
}: SubmissionLoaderParams): Promise<SubmissionLoaderResponse> {
  try {
    // Use token from route params (new format) or search params (backward compat)
    const effectiveToken = routeToken || searchToken;

    const { settings, submission } = await getSubmissionData(accountSlug, submissionId, userType, effectiveToken);

    // Remove token from URL after successful verification to clean up browser history
    // Using `replace: true` ensures the token URL doesn't remain in browser history
    if (effectiveToken) {
      // Redirect to clean URL without token
      // Default to base submission route, but allow custom redirect path (e.g., for signer route)
      const targetPath = redirectPath
        ? redirectPath(accountSlug, submissionId)
        : `/${accountSlug}/${submissionId}`;
      throw redirect({
        to: targetPath,
        replace: true,
      });
    }

    return {
      settings,
      submission,
    };
  } catch (error) {
    // Let React Router handle redirects — don't report them as errors
    if (error instanceof Response && error.status >= 300 && error.status < 400) {
      throw error;
    }
    if (isNotFoundError(error) && error.code === NotFoundErrorCode.SettingsNotFound) {
      throw redirect({ to: '/' });
    }

    if (isUnauthorizedError(error) || isBadRequestError(error) || isNotFoundError(error)) {
      if (!isUnauthorizedError(error)) {
        window.DD_RUM?.addError(error, {
          source: 'submission-loader',
          accountSlug,
          submissionId,
        });
      }
      try {
        const settings = await getSettingsByUrlSlug(accountSlug);
        return {
          settings,
          error,
        };
      } catch (settingsError) {
        window.DD_RUM?.addError(settingsError, {
          source: 'submission-loader-settings-fallback',
          accountSlug,
          submissionId,
        });
        throw redirect({ to: '/' });
      }
    }

    window.DD_RUM?.addError(error, {
      source: 'submission-loader-unhandled',
      accountSlug,
      submissionId,
    });
    throw error;
  }
}

/**
 * Returns route configuration options for submission routes
 * Can be spread directly in the route configuration
 */
export function submissionRouteLoader (userType: UserType, redirectPath?: (accountSlug: string, submissionId: string) => string) {
  return {
    // Extract loader dependencies - fallback to search for backward compat (delete after this has been deployed for a while)
    loaderDeps: ({ search }: { search: SubmissionSearchParams }): SubmissionSearchParams => ({
      token: search.token,
    }),

    // Load data using the validated params
    loader: async ({
      params, deps,
    }: { params: { accountSlug: string; submissionId: string; token?: string }, deps: SubmissionSearchParams }) => {
      return submissionLoader({
        params,
        deps,
        userType,
        redirectPath,
      });
    },
  };
}

/**
 * Helper function to construct a submission URL with token path param
 */
export function buildSubmissionUrl (accountSlug: string, submissionId: string, token?: string): string {
  const basePath = `/${accountSlug}/${submissionId}`;
  if (!token) {
    return basePath;
  }
  return `${basePath}/${token}`;
}
